import api from "./api";
import type { User, Department, Organization } from "@/types/user";

export interface UserListParams {
  page?: number;
  page_size?: number;
  department_id?: number;
  user_type?: string;
  is_active?: boolean;
  status?: "active" | "inactive";
  employment_status?: "active" | "resigned";
  exclude_leave?: boolean;
  organization_id?: number;
  search?: string;
  include_subordinates?: boolean;
  include_managed?: boolean; // 대리 관리 직원(미가입) 포함 여부 — 기본 제외, 행정 화면만 true
}

export interface UserListResponse {
  items: User[];
  total: number;
  page: number;
  page_size: number;
}

export interface UserUpdateData {
  name?: string;
  phone?: string;
  position?: string;
  job_title?: string;
  department_id?: number;
  role?: string;
  is_active?: boolean;
}

export interface DepartmentCreateData {
  name: string;
  code: string;
  parent_id?: number;
  manager_id?: string;
  order_num?: number;
}

export const userService = {
  // Users
  async getUsers(params?: UserListParams): Promise<User[]> {
    const response = await api.get<UserListResponse>("/admin/users", { params });
    return response.data.items;
  },

  // 담당자 선택용 - 모든 활성 사용자 조회 (page_size max=100)
  async getAllUsers(): Promise<User[]> {
    const response = await api.get<UserListResponse>("/admin/users", {
      params: { page_size: 100, status: "active" },
    });
    return response.data.items;
  },

  // 직원 목록 조회 (일반 사용자도 접근 가능 - /users 엔드포인트 사용)
  // 출장 참여자 선택 등에 사용 - 휴직자 제외
  async getActiveEmployees(params?: Partial<UserListParams>): Promise<User[]> {
    const response = await api.get<User[]>("/users", {
      params: {
        page_size: 100,
        user_type: "employee",
        is_active: true,
        employment_status: "active",
        exclude_leave: true,
        ...params,
      },
    });
    return response.data;
  },

  // 내 부서 및 하위 부서 직원 조회 (업무 하달용)
  async getMyDepartmentEmployees(): Promise<User[]> {
    // 먼저 현재 사용자 정보를 가져와서 department_id 확인
    const currentUserResponse = await api.get<User>("/auth/me");
    const currentUser = currentUserResponse.data;

    if (!currentUser.department_id) {
      // 부서가 없는 경우 활성 직원 전체 반환
      return this.getActiveEmployees();
    }

    const response = await api.get<User[]>("/users", {
      params: {
        page_size: 100,
        user_type: "employee",
        is_active: true,
        employment_status: "active",
        exclude_leave: true,
        department_id: currentUser.department_id,
        include_subordinates: true,
      },
    });
    return response.data;
  },

  // 업무 하달/위임 대상 후보 조회 (조직도 기준 한 단계 아래 + 범위 위임 대상)
  // - 하위 부서가 있으면 그 부서장들, 말단 팀이면 내 부서원
  // - 범위 위임으로 열린 대상은 via_scope_grant=true (화면에서 '위임' 뱃지)
  async getAssignmentCandidates(): Promise<User[]> {
    const response = await api.get<User[]>("/users/assignment-candidates");
    return response.data;
  },

  // 긴급 업무 하달 대상 후보 조회 (조직도 하위 전체 + 범위 위임 조직 전원)
  // - 직속 단계를 건너뛰고 본인 하위 조직의 모든 직원
  async getSubordinateCandidates(): Promise<User[]> {
    const response = await api.get<User[]>("/users/subordinate-candidates");
    return response.data;
  },

  // 직원 목록 조회 (성과 배분 등에 사용)
  async getEmployees(search?: string): Promise<User[]> {
    const response = await api.get<User[]>("/users", {
      params: {
        page_size: 100,
        user_type: "employee",
        is_active: true,
        employment_status: "active",
        exclude_leave: true,
        search: search || undefined,
      },
    });
    return response.data;
  },

  async getUser(userId: string): Promise<User> {
    const response = await api.get<User>(`/users/${userId}`);
    return response.data;
  },

  async updateUser(userId: string, data: UserUpdateData): Promise<User> {
    const response = await api.put<User>(`/users/${userId}`, data);
    return response.data;
  },

  async deactivateUser(userId: string): Promise<void> {
    await api.delete(`/users/${userId}`);
  },

  // Departments
  async getDepartments(): Promise<Department[]> {
    const response = await api.get<Department[]>("/users/departments");
    return response.data;
  },

  async createDepartment(data: DepartmentCreateData): Promise<Department> {
    const response = await api.post<Department>("/users/departments", data);
    return response.data;
  },

  // Organizations (고객사)
  async getOrganizations(): Promise<Organization[]> {
    const response = await api.get<{ items: Organization[]; total: number }>(
      "/customer-organizations",
      { params: { page_size: 100, is_active: true } }
    );
    return response.data.items;
  },
};

export default userService;
